import React, { Component, type ComponentType, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { JswShareButton } from '@atlassian/jira-software-report-header-common/src/ui/share-button/index.tsx';
import {
	REPORT_BURNUP,
	REPORT_BURNDOWN,
	REPORT_CUMULATIVE_FLOW_DIAGRAM,
	REPORT_VELOCITY,
} from '../../common/constants';
import type { Report as ReportType } from '../../common/types';
import messages from './messages';

type Props = Intl & {
	reportType: ReportType;
	boardId: number;
	BreadcrumbRenderer: ComponentType;
	Chevron: ComponentType<{
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		onClick: (arg1: SyntheticEvent<any>, arg2: UIAnalyticsEvent) => void;
		showHowTo: boolean;
	}>;
	HowTo: ComponentType<{
		visible: boolean;
		reportType: ReportType;
	}>;
	HowToReadText: ComponentType<{
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		onClick: (arg1: SyntheticEvent<any>, arg2: UIAnalyticsEvent) => void;
	}>;
};

type State = {
	showHowTo: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
export class Header extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			showHowTo: false,
		};
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onToggleHowToPane = (_: SyntheticEvent<any>, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'report-page-how-to', {
			isOpening: !this.state.showHowTo,
		});
		this.setState({
			showHowTo: !this.state.showHowTo,
		});
	};

	renderTitle(): string | null {
		const {
			intl: { formatMessage },
			reportType,
		} = this.props;

		if (reportType === REPORT_VELOCITY) {
			return formatMessage(messages.velocityReportTitle);
		}

		if (reportType === REPORT_BURNUP) {
			return formatMessage(messages.burnupReportTitle);
		}

		if (reportType === REPORT_CUMULATIVE_FLOW_DIAGRAM) {
			return formatMessage(messages.cumulativeFlowTitle);
		}

		if (reportType === REPORT_BURNDOWN) {
			return formatMessage(messages.burndownTitle);
		}

		return null;
	}

	render() {
		const { BreadcrumbRenderer, HowTo, Chevron, HowToReadText, boardId } = this.props;

		const breadcrumbs = <BreadcrumbRenderer />;
		return (
			<Wrapper>
				<PageHeader
					truncateTitle
					breadcrumbs={breadcrumbs}
					actions={
						<ActionsContainer>
							<HowToReadContainer>
								<Chevron onClick={this.onToggleHowToPane} showHowTo={this.state.showHowTo} />
								<HowToReadText onClick={this.onToggleHowToPane} />
							</HowToReadContainer>
							<Box paddingInlineStart="space.200">
								<JswShareButton boardId={boardId} />
							</Box>
						</ActionsContainer>
					}
				>
					<HeadingStyle>
						<TruncatingTextStyle>{this.renderTitle()}</TruncatingTextStyle>
					</HeadingStyle>
				</PageHeader>

				<HowTo visible={this.state.showHowTo} reportType={this.props.reportType} />
			</Wrapper>
		);
	}
}

const HeaderWithIntl = injectIntl(Header);

export default HeaderWithIntl;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HowToReadContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingStyle = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TruncatingTextStyle = styled.div({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	flex: '0 1 auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	padding: `0 ${token('space.400', '32px')}`,
});
